import { useState, useCallback } from 'react'
import {
  TypeId,
  useDeleteApiV1GroupsByGroupIdMembersAndMemberIdMutation,
} from '@wellesley/openapi'
import { useCurrentUser } from '@wellesley/user'
import { toast } from '@wellesley/system-toast'
import ConfirmModal from 'components/modals/ConfirmModal'
import { getErrorMsg } from '@wellesley/error-kit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import useGroupMembers from '../useGroupMembers'

type UseLeaveGroupProps = {
  groupId: TypeId
  onLeave?: () => void
}

// TODO: Implement real implementation of leaving a group after backend will add new enpoint
// https://wellesley.youtrack.cloud/issue/WLSY-2408/Groups-add-leave-group-endpoint

const useLeaveGroup = ({ groupId, onLeave }: UseLeaveGroupProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { user } = useCurrentUser()
  const [deleteMember, { isLoading: isDeleting }] =
    useDeleteApiV1GroupsByGroupIdMembersAndMemberIdMutation()
  const { refetch: refetchGroupMembers } = useGroupMembers({ groupId })

  const handleLeaveGroup = useCallback(async () => {
    if (!user?.id) return

    try {
      await deleteMember({ groupId, memberId: user.id })
      toast.error('Real implementation of leaving a group coming soon')
      setIsDialogOpen(false)
      onLeave?.()
      await refetchGroupMembers()
    } catch (e) {
      toast.error(
        `Couldn't leave group: ${getErrorMsg(e as FetchBaseQueryError)}`,
      )
    }
  }, [deleteMember, groupId, user?.id, onLeave, refetchGroupMembers])

  const leaveGroup = useCallback(() => {
    setIsDialogOpen(true)
  }, [])

  const handleCancel = useCallback(() => {
    setIsDialogOpen(false)
  }, [])

  const LeaveGroupDialogComponent = useCallback(() => {
    if (!user?.id) return null

    return (
      <ConfirmModal
        open={isDialogOpen}
        onClose={handleCancel}
        title="Are you sure you want to leave this group?"
        leftButton={{
          title: 'Cancel',
          variant: 'secondary',
          isLoading: false,
          onClick: handleCancel,
        }}
        rightButton={{
          title: 'Leave Group',
          onClick: () => void handleLeaveGroup(),
          variant: 'red_primary',
          isLoading: isDeleting,
        }}
      />
    )
  }, [isDialogOpen, handleCancel, handleLeaveGroup, isDeleting])

  return {
    leaveGroup,
    LeaveGroupDialog: LeaveGroupDialogComponent,
  }
}

export default useLeaveGroup
